/**
 * Metadata View Feature
 *
 * This file contains all logic for the metadata/debug info view feature.
 * To disable this feature, set ENABLED to false.
 */
import React, { useState, useRef } from 'react';
import { IoInformationCircleOutline } from 'react-icons/io5';
import { Z_INDEX, useClickOutside, getPopoverPosition } from '../utils/tooltips';

// Feature toggle - set to false to disable the entire feature
export const ENABLED = true;

// Known metadata fields with special types and display names
const KNOWN_FIELDS = {
  '_status': { name: 'Status', type: 'metadata_status' },
  'httpCode': { name: 'HTTP', type: 'metadata_http' },
  'elementResults': { name: 'Elements', type: 'element_results' },
  'title': { name: 'Page Title', type: 'text' },
  'duration': { name: 'Duration', type: 'metadata_duration' },
  'attempts': { name: 'Attempts', type: 'number' },
  '_error': { name: 'Error', type: 'metadata_error' },
  'blockerType': { name: 'Blocker', type: 'text' },
  'blockedReason': { name: 'Block Reason', type: 'text' },
  '_extractedAt': { name: 'Extracted At', type: 'date' },
};

// Preferred column order for known fields (others will appear after)
const PREFERRED_ORDER = [
  '_status', 'httpCode', 'elementResults', 'title', 'duration',
  '_error', 'blockerType', 'blockedReason', '_extractedAt', 'attempts'
];

// Fields to hide from the metadata view
const HIDDEN_FIELDS = new Set(['urlIndex', 'extractedAt', '_httpCode', 'http']);

/**
 * Process data for metadata view - returns metadata columns and transformed rows
 */
export const processMetadataView = (data) => {
  // Collect all unique metadata keys from the data
  const allMetadataKeys = new Set();
  data.forEach(row => {
    if (row.metadata) {
      Object.keys(row.metadata).forEach(key => allMetadataKeys.add(key));
    }
  });

  // Filter out hidden fields, then sort: preferred order first, then alphabetically
  const sortedKeys = Array.from(allMetadataKeys).filter(key => !HIDDEN_FIELDS.has(key)).sort((a, b) => {
    const aIndex = PREFERRED_ORDER.indexOf(a);
    const bIndex = PREFERRED_ORDER.indexOf(b);
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return a.localeCompare(b);
  });

  // Build columns from discovered keys
  const metadataColumns = sortedKeys.map(key => {
    const known = KNOWN_FIELDS[key];
    if (known) {
      return { id: key, name: known.name, type: known.type };
    }
    // Auto-generate name from key (convert camelCase/snake_case to Title Case)
    const autoName = key
      .replace(/^_/, '')
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase())
      .trim();
    return { id: key, name: autoName, type: 'text' };
  });

  // Transform rows to use metadata as data source
  const metadataRows = data.map(row => ({
    ...row,
    data: row.metadata || {},
  }));

  return {
    displayColumns: metadataColumns,
    processedData: metadataRows,
    metadataColumnCount: metadataColumns.length,
  };
};

/**
 * Status color configuration
 */
const STATUS_CONFIG = {
  completed: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', icon: '✓' },
  pending: { bg: 'bg-gray-500/15', text: 'text-gray-400', icon: '○' },
  in_progress: { bg: 'bg-blue-500/15', text: 'text-blue-400', icon: '◔' },
  blocked: { bg: 'bg-orange-500/15', text: 'text-orange-400', icon: '⚠' },
  failed: { bg: 'bg-red-500/15', text: 'text-red-400', icon: '✗' },
  retrying: { bg: 'bg-yellow-500/15', text: 'text-yellow-400', icon: '↻' },
};

/**
 * Element Results Popover Component - click to toggle
 */
const ElementResultsPopover = ({ value, allSuccess, failCount, isDarkMode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const popoverRef = useRef(null);

  useClickOutside([buttonRef, popoverRef], () => setIsOpen(false), isOpen);

  const handleClick = (e) => {
    e.stopPropagation();
    if (!isOpen && buttonRef.current) {
      setPosition(getPopoverPosition(buttonRef.current.getBoundingClientRect(), 500));
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={handleClick}
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium cursor-pointer transition-colors ${
          allSuccess
            ? isDarkMode ? "bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
            : isDarkMode ? "bg-orange-500/15 text-orange-400 hover:bg-orange-500/25" : "bg-orange-50 text-orange-700 hover:bg-orange-100"
        }`}
      >
        <span>{value.length} elements</span>
        {!allSuccess && <span>({failCount} failed)</span>}
      </button>
      {isOpen && (
        <div
          ref={popoverRef}
          style={{ top: position.top, left: position.left, zIndex: Z_INDEX.POPOVER }}
          className={`fixed p-3 rounded-lg shadow-xl min-w-[320px] max-w-[480px] max-h-[400px] overflow-auto ${
            isDarkMode
              ? "bg-zinc-900 border border-zinc-700"
              : "bg-white border border-gray-200 shadow-lg"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="space-y-2">
            {value.map((el, idx) => (
              <div
                key={idx}
                className={`p-2 rounded-md ${
                  isDarkMode ? "bg-zinc-800/50" : "bg-gray-50"
                }`}
              >
                {/* Header row: name, action, status */}
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`text-xs font-medium truncate ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
                      {el.elementName || `Element ${idx + 1}`}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      el.action === 'click'
                        ? isDarkMode ? "bg-blue-500/20 text-blue-400" : "bg-blue-100 text-blue-700"
                        : isDarkMode ? "bg-violet-500/20 text-violet-400" : "bg-violet-100 text-violet-700"
                    }`}>
                      {el.action}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {el.duration && (
                      <span className={`text-[10px] font-mono ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                        {(el.duration / 1000).toFixed(1)}s
                      </span>
                    )}
                    <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${
                      el.success
                        ? isDarkMode ? "text-emerald-400" : "text-emerald-600"
                        : isDarkMode ? "text-red-400" : "text-red-600"
                    }`}>
                      {el.success ? "✓" : "✗"}
                    </span>
                  </div>
                </div>
                {/* Details row */}
                <div className={`text-[11px] ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                  {el.error ? (
                    <span className={isDarkMode ? "text-red-400" : "text-red-600"}>{el.error}</span>
                  ) : el.extraction ? (
                    <div className="space-y-0.5">
                      <div>{el.extraction.reason}</div>
                      {el.extraction.columnsExtracted > 0 && (
                        <div className={isDarkMode ? "text-emerald-400" : "text-emerald-600"}>
                          +{el.extraction.columnsExtracted} columns extracted
                        </div>
                      )}
                      {el.extraction.selectorUsed && (
                        <div className={`font-mono text-[10px] truncate ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                          {el.extraction.selectorType}: {el.extraction.selectorUsed}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span>Type: {el.type}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * JSON Popover Component - click to toggle
 */
const JsonPopover = ({ value, isDarkMode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const popoverRef = useRef(null);
  const isArray = Array.isArray(value);
  const preview = isArray ? `[${value.length} items]` : 'Object';

  useClickOutside([buttonRef, popoverRef], () => setIsOpen(false), isOpen);

  const handleClick = (e) => {
    e.stopPropagation();
    if (!isOpen && buttonRef.current) {
      setPosition(getPopoverPosition(buttonRef.current.getBoundingClientRect(), 450));
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={handleClick}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium cursor-pointer transition-colors ${
          isDarkMode
            ? "bg-violet-500/15 text-violet-400 hover:bg-violet-500/25"
            : "bg-violet-50 text-violet-700 hover:bg-violet-100"
        }`}
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
        </svg>
        {preview}
      </button>
      {isOpen && (
        <div
          ref={popoverRef}
          style={{ top: position.top, left: position.left, zIndex: Z_INDEX.POPOVER }}
          className={`fixed px-2.5 py-1.5 text-[11px] rounded shadow-lg max-w-md max-h-64 overflow-auto font-mono ${
            isDarkMode
              ? "bg-zinc-800 text-gray-200 border border-zinc-700"
              : "bg-white text-gray-800 border border-gray-200 shadow-lg"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <pre className="whitespace-pre-wrap select-text">{JSON.stringify(value, null, 2)}</pre>
        </div>
      )}
    </div>
  );
};

/**
 * Render metadata cell based on column type
 * Returns null if the column type is not a metadata type (caller should handle normally)
 */
export const renderMetadataCell = (columnType, value, isDarkMode) => {
  // Handle metadata status column
  if (columnType === 'metadata_status') {
    if (!value) return { handled: true, element: <span className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>—</span> };
    const config = STATUS_CONFIG[value] || STATUS_CONFIG.pending;
    return {
      handled: true,
      element: (
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
          <span>{config.icon}</span>
          {value}
        </span>
      )
    };
  }

  // Handle metadata HTTP code column
  if (columnType === 'metadata_http') {
    if (!value) return { handled: true, element: <span className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>—</span> };
    const codeClass = value >= 200 && value < 300
      ? 'bg-emerald-500/15 text-emerald-400'
      : value >= 400
      ? 'bg-red-500/15 text-red-400'
      : 'bg-gray-500/15 text-gray-400';
    return {
      handled: true,
      element: (
        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-mono font-medium ${codeClass}`}>
          {value}
        </span>
      )
    };
  }

  // Handle metadata duration column (ms to readable)
  if (columnType === 'metadata_duration') {
    if (!value) return { handled: true, element: <span className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>—</span> };
    const seconds = (value / 1000).toFixed(1);
    return {
      handled: true,
      element: (
        <span className={`text-xs font-mono ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
          {seconds}s
        </span>
      )
    };
  }

  // Handle metadata error column
  if (columnType === 'metadata_error') {
    if (!value) return { handled: true, element: <span className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>—</span> };
    const maxLength = 50;
    const truncated = value.length > maxLength;
    return {
      handled: true,
      element: (
        <span className={`text-xs ${isDarkMode ? "text-red-400" : "text-red-600"}`}>
          {truncated ? value.substring(0, maxLength) + '...' : value}
        </span>
      )
    };
  }

  // Handle element results (special view for extraction results)
  if (columnType === 'element_results') {
    if (!value || !Array.isArray(value) || value.length === 0) {
      return { handled: true, element: <span className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>—</span> };
    }
    const successCount = value.filter(el => el.success).length;
    const failCount = value.length - successCount;
    const allSuccess = failCount === 0;
    return {
      handled: true,
      element: <ElementResultsPopover value={value} allSuccess={allSuccess} failCount={failCount} isDarkMode={isDarkMode} />
    };
  }

  // Handle JSON/array column (generic objects)
  if (columnType === 'json') {
    if (!value) return { handled: true, element: <span className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>—</span> };
    return {
      handled: true,
      element: <JsonPopover value={value} isDarkMode={isDarkMode} />
    };
  }

  // Not a metadata column type
  return { handled: false, element: null };
};

/**
 * Metadata toggle button icon (SVG)
 */
export const MetadataIcon = () => <IoInformationCircleOutline className="w-4 h-4" />;
